import * as actions from "actions/mutate";
import { MUTATE_OPERATION, NOOP } from "constants/action-types";
import { Observable } from "rxjs";
import patch from "immutablepatch";
import {
    getValueFromLocalStorage,
    setValueToLocalStorage,
    mutateSnapshot,
    getMutateHistory,
    batchMutations,
} from "helpers/mutable";
import { NAMESPACE_KEYS } from "constants/mutable";
import me from "models/me";
import * as SentryUtil from "lib/sentry-util";
import { AssortmentTransactions, AssortmentEvents } from "constants/sentry-metrics";
import { ALLOCATION_BAG_NAMESPACE, ITEMS_NAMESPACE } from "constants/product-collection";
import { getMutationBatchSizeByObjectId } from "helpers/collection";

const getAndIncrementRequestId = (collectionId) => {
    let requestId = parseInt(getValueFromLocalStorage(collectionId, me._id, NAMESPACE_KEYS.REQUEST, 0), 10);
    if (isNaN(requestId)) {
        requestId = 0;
    }
    requestId = requestId + 1;
    setValueToLocalStorage(collectionId, me._id, NAMESPACE_KEYS.REQUEST, requestId);
    return requestId.toString();
};

const getOperationKey = (operation) =>
    [
        operation.operation,
        ...(operation.childKey ? [operation.childKey] : []),
        operation.index === undefined ? operation.key : operation.index,
    ].join("_");

// Grab a mutation, buffer and add history and bump version
let historyGroupId = 1;
export const enhanceMutation = (action$, store) => {
    let buffers: any = {};
    return (
        action$
            .ofType(MUTATE_OPERATION)
            // combine all mutation operations with the same objectId & mutableType
            .map(({ payload, meta }) => {
                const groupKey = `${payload.objectId}_${payload.mutableType}`;
                if (buffers[groupKey] === undefined) {
                    buffers[groupKey] = {
                        ...payload,
                        meta,
                    };
                } else {
                    // combine operations
                    buffers[groupKey].operations = buffers[groupKey].operations.concat(...payload.operations);
                }
                return meta;
            })
            // buffer mutations on every interval if buffering enabled
            .debounce((meta) => (meta.buffer ? Observable.interval(50) : Observable.empty()))
            // combine observables into one stream
            .flatMap(() => {
                historyGroupId++;
                // clear for next batch
                const savedBuffers = buffers;
                buffers = {};

                const state = store.getState();

                // collapse object containing double array to single array grouped by objectId
                const mutations: any[] = Object.keys(savedBuffers).reduce((acc, key) => {
                    // Only batch when:
                    // - Object being mutated is Items list
                    // - Assortment uses allocation dimensions

                    const batchedMutations = batchMutations(
                        savedBuffers[key].objectId,
                        savedBuffers[key],
                        getMutationBatchSizeByObjectId(savedBuffers[key].objectId)
                    );
                    return acc.concat(batchedMutations);
                }, []);
                // combine operations
                return Observable.from(mutations).map((payload, index) => {
                    const {
                        mutate: { HEADs, snapshots },
                    } = state;
                    const HEAD = HEADs.get(payload.objectId);
                    const snapshot = snapshots.get(payload.objectId);

                    // return no-op if state has been cleared
                    if (!HEAD || !snapshot) {
                        return {
                            type: NOOP,
                        };
                    }

                    if (HEAD.object_type === ITEMS_NAMESPACE) {
                        SentryUtil.startTransaction(AssortmentTransactions.Mutate);
                    }

                    if (HEAD.object_type === ALLOCATION_BAG_NAMESPACE) {
                        SentryUtil.startTransaction(AssortmentTransactions.MutateAllocationBag);
                    }

                    // if there are multiple set operations, combine the deltas
                    // if there are multiple insert operations, we want to preserve the order if it's provided
                    const setIndexes = {};
                    const operations = [];
                    payload.operations.forEach((operation) => {
                        if (
                            operation.deltas &&
                            operation.operation === "set" &&
                            payload.mutableType === actions.MutableType.List
                        ) {
                            const key = getOperationKey(operation);
                            if (setIndexes[key] === undefined) {
                                setIndexes[key] = operations.length;
                                operations.push(operation);
                            } else {
                                const firstOperation = operations[setIndexes[key]];

                                // Necessary evil because of how `bulks` table content is set.
                                // Each time cell change event, the resulting mutate operation contains ALL changes applied to the bag instead of only partial changes,
                                // this makes immutablediff generates all diff operations against original bag.
                                //
                                // When rapid cell editing (debounced mutate operations), each operation has same deltas, which in "op=add" case when patched to a list,
                                // will append instead of replace.
                                const operationDeltas = operation.deltas.filter(
                                    (delta) =>
                                        !(
                                            delta.get("path", "").match(/\/bulks\/r/i) &&
                                            firstOperation.deltas.some((baseDelta) => baseDelta.equals(delta))
                                        )
                                );

                                firstOperation.value = patch(firstOperation.value, operationDeltas);
                                firstOperation.deltas = firstOperation.deltas.concat(operationDeltas);
                            }
                        } else if (
                            operation.operation === "insert" &&
                            typeof operation.index === "number" &&
                            !operations[operation.index] &&
                            payload.mutableType === actions.MutableType.List
                        ) {
                            operations[operation.index] = operation;
                        } else {
                            operations.push(operation);
                        }
                    });

                    // add operations, HEAD, snapshot for mutateSnapshot, getMutateHistory
                    payload = { ...payload, operations, HEAD, snapshot };

                    const { objectId } = payload;
                    const newHEAD = {
                        ...HEAD,
                        revision: parseInt(HEAD.revision, 10) + 1,
                    };
                    const requestId = getAndIncrementRequestId(objectId);
                    const mutatedSnapshot = mutateSnapshot(snapshot, payload);
                    const { up, down } = getMutateHistory(snapshot, payload);
                    const { meta } = payload;

                    // remove for dispatch
                    delete payload.meta;

                    if (HEAD.object_type === ITEMS_NAMESPACE) {
                        SentryUtil.startSpan(AssortmentTransactions.Mutate, AssortmentEvents.MutateEnhanced);
                    }

                    if (HEAD.object_type === ALLOCATION_BAG_NAMESPACE) {
                        SentryUtil.startSpan(
                            AssortmentTransactions.MutateAllocationBag,
                            AssortmentEvents.MutateEnhanced
                        );
                    }

                    // dispatch enhance mutation operation
                    return actions.enhanceMutateOperation(
                        {
                            ...payload,
                            up,
                            down,
                            HEAD: newHEAD,
                            oldSnapshot: payload.snapshot,
                            snapshot: mutatedSnapshot,
                            requestId,
                            historyGroupId,
                        },
                        {
                            ...meta,
                            batchSize: mutations.length,
                        }
                    );
                });
            })
    );
};
